/**
 * Enterprise Lesson Engine — step sequencer for guided enterprise training.
 * Advances steps, listens for triggers, orchestrates lockdown/highlight.
 */
import type { EnterpriseLessonDef, EnterpriseStep, EnterpriseTask, SimEventMap } from '@graphicsim/shared-types';
import { eventBus } from './eventBus';
import { applyLockdown, removeLockdown } from './uiLockdown';
import { applyHighlight, removeHighlight } from './uiHighlight';
import { playStepAudio, stopAudio } from './enterpriseAudio';
import { enterpriseStore } from '../stores/enterpriseStore';
import { projectStore } from '../stores/projectStore';
import { onFocusSim, broadcastLessonComplete } from './videoBoardChannel';
import { preloadVideoUrl, isVideoReady, cleanupPreloadedVideos } from './videoPreloader';

let activeLessonDef: EnterpriseLessonDef | null = null;
let currentUnsubscribe: (() => void) | null = null;
let focusSimCleanup: (() => void) | null = null;
let delayTimer: ReturnType<typeof setTimeout> | null = null;
let onStepActivated: ((sectionIndex: number, stepIndex: number, step: EnterpriseStep) => void) | null = null;
let onLessonComplete: (() => void) | null = null;
let onActionReady: (() => void) | null = null;
let checklistUnsubscribes: Array<() => void> = [];

export function setOnStepActivated(cb: (sectionIndex: number, stepIndex: number, step: EnterpriseStep) => void): void {
  onStepActivated = cb;
}

export function setOnLessonComplete(cb: () => void): void {
  onLessonComplete = cb;
}

export function setOnActionReady(cb: () => void): void {
  onActionReady = cb;
}

export function startEnterpriseLesson(lessonDef: EnterpriseLessonDef): void {
  // Clear any leftover project items from Free Play or previous lessons
  projectStore.getState().resetProject();

  // Preload video assets so they're buffered before the user reaches video steps
  if (lessonDef.preloadUrls) {
    for (const url of lessonDef.preloadUrls) {
      preloadVideoUrl(url);
    }
  }

  activeLessonDef = lessonDef;

  if (lessonDef.mode === 'checklist') {
    startChecklistRun(lessonDef);
    return;
  }

  const sections = lessonDef.sections ?? [];
  const totalSteps = sections.reduce((sum, s) => sum + s.steps.length, 0);
  enterpriseStore.getState().startLesson(lessonDef.id, totalSteps);

  const state = enterpriseStore.getState();
  activateStep(state.currentSectionIndex, state.currentStepIndex);
}

function startChecklistRun(lessonDef: EnterpriseLessonDef): void {
  const tasks = lessonDef.tasks ?? [];
  enterpriseStore.getState().startLesson(lessonDef.id, tasks.length);

  // Group tasks by eventName so a single event only marks ONE task complete
  // (the first uncompleted match in task order). This handles the common case
  // where the checklist contains duplicate triggers like "zone:assignmentChanged".
  const tasksByEvent: Record<string, EnterpriseTask[]> = {};
  for (const task of tasks) {
    const key = task.completionTrigger.eventName as string;
    (tasksByEvent[key] ??= []).push(task);
  }

  for (const [eventName, group] of Object.entries(tasksByEvent)) {
    const handler = (payload: any) => {
      const completedSet = new Set(enterpriseStore.getState().completedStepIds);
      for (const task of group) {
        if (completedSet.has(task.id)) continue;
        if (!matchesTrigger(task.completionTrigger, payload)) continue;
        enterpriseStore.getState().completeStep(task.id);
        maybeFinishChecklist();
        return;
      }
    };
    const off = eventBus.on(eventName as keyof SimEventMap, handler as any);
    checklistUnsubscribes.push(off);
  }

  // If user returns to a fully-completed lesson, mark it done immediately.
  maybeFinishChecklist();
}

/** Walk a dot-separated path against a payload, e.g. "item.mediaType" → payload.item.mediaType. */
function resolvePath(payload: any, path: string): any {
  return path.split('.').reduce<any>((obj, key) => (obj == null ? obj : obj[key]), payload);
}

/** Compare an actual value against a matchValue spec, supporting `contains:` and `oneOf:` prefixes. */
function compareValue(actual: string, expected: string): boolean {
  if (expected.startsWith('contains:')) {
    return actual.toLowerCase().includes(expected.slice(9).toLowerCase());
  }
  if (expected.startsWith('oneOf:')) {
    const opts = expected.slice(6).toLowerCase().split('|');
    return opts.includes(actual.toLowerCase());
  }
  return actual === expected;
}

function matchesTrigger(trigger: EnterpriseTask['completionTrigger'], payload: any): boolean {
  if (!trigger.matchField) return true;

  // Virtual field: resolve the payload's itemId to its Project panel name. Events like
  // monitor:takeToProgram only carry an id, so without this a step that says "play the
  // scoreboard" is satisfied by playing anything at all.
  if (trigger.matchField === 'itemName') {
    const name = payload?.itemId ? projectStore.getState().getItem(payload.itemId)?.name : undefined;
    if (trigger.matchValue === undefined) return true;
    return compareValue(String(name ?? ''), trigger.matchValue);
  }

  let val = resolvePath(payload, trigger.matchField);
  // For top-level object values without a nested path, fall back to .name (back-compat for
  // `matchField: 'item'` patterns that match against the item's display name).
  if (val && typeof val === 'object' && !trigger.matchField.includes('.') && 'name' in val) {
    val = (val as any).name;
  }
  if (trigger.matchValue === undefined) return true;
  return compareValue(String(val ?? ''), trigger.matchValue);
}

function maybeFinishChecklist(): void {
  if (!activeLessonDef || activeLessonDef.mode !== 'checklist') return;
  const total = activeLessonDef.tasks?.length ?? 0;
  const done = enterpriseStore.getState().completedStepIds.length;
  if (total > 0 && done >= total) {
    completeEntireLesson();
  }
}

export function activateStep(sectionIndex: number, stepIndex: number): void {
  if (!activeLessonDef) return;
  const sections = activeLessonDef.sections ?? [];

  // Cleanup previous step
  cleanup();

  const section = sections[sectionIndex];
  if (!section) {
    completeEntireLesson();
    return;
  }

  const step = section.steps[stepIndex];
  if (!step) {
    // Section exhausted — move to next section
    const nextSection = sectionIndex + 1;
    if (nextSection < sections.length) {
      activateStep(nextSection, 0);
    } else {
      completeEntireLesson();
    }
    return;
  }

  enterpriseStore.getState().advanceStep(sectionIndex, stepIndex);

  // Apply lockdown.
  //
  // Action steps: only this step's own targets stay clickable.
  // Info steps: the whole sim is blocked (undimmed, so the panels stay readable) —
  // the instruction panel's Next button is the only way forward. Without this the
  // user can run ahead while an info card is up and perform later steps out of
  // order, which leaves those steps' triggers with nothing left to fire on.
  const panelSelectors = ['.ent-breadcrumb-bar', '.ent-instruction-panel'];
  if (step.type === 'action') {
    if (step.allowedSelectors) {
      applyLockdown([...step.allowedSelectors, ...panelSelectors]);
    } else {
      // No targets declared — locking would make the step uncompletable, so leave
      // the sim open and surface the authoring gap instead.
      console.warn('[EnterpriseLesson] Action step has no allowedSelectors, UI left unlocked:', step.id);
    }
  } else {
    applyLockdown(panelSelectors, { dim: false });
  }

  // Apply highlight
  if (step.highlightSelector) {
    applyHighlight(step.highlightSelector);
  }

  // Mark elements as visually-disabled for this step (e.g., wrong-answer library tiles)
  if (step.disabledSelectors) {
    applyDisabled(step.disabledSelectors);
  }

  // Play audio
  playStepAudio(step.audioUrl);

  // Notify UI
  if (onStepActivated) {
    onStepActivated(sectionIndex, stepIndex, step);
  }

  // Set up completion trigger
  if (step.type === 'action' && step.completionTrigger) {
    const trigger = step.completionTrigger;

    // Special case: BroadcastChannel focus-sim
    if (trigger.eventName === 'ui:videoBoardOpened' && trigger.matchField === '_focusSim') {
      // Listen for focus-sim BroadcastChannel message
      let resolved = false;
      const handleFocus = () => {
        if (resolved) return;
        resolved = true;
        completeCurrentStep(sectionIndex, stepIndex);
      };
      onFocusSim(handleFocus);
      focusSimCleanup = () => {
        // Can't truly remove BroadcastChannel listeners, but flag prevents action
        resolved = true;
      };
      return;
    }

    const distinctSeen = new Set<string>();
    const handler = (payload: any) => {
      if (!matchesTrigger(trigger, payload)) return;

      // Distinct-value gating: don't complete until N unique values of distinctField seen
      if (trigger.distinctField && trigger.count && trigger.count > 0) {
        const distinctVal = resolvePath(payload, trigger.distinctField);
        if (distinctVal === undefined || distinctVal === null) return;
        distinctSeen.add(String(distinctVal));
        if (distinctSeen.size < trigger.count) return;
      }

      const doComplete = () => {
        // Trigger satisfied — but if step requires explicit Next click,
        // unsubscribe and let the user finish their edits at their own pace.
        if (step.requireNextClick) {
          if (currentUnsubscribe) { currentUnsubscribe(); currentUnsubscribe = null; }
          if (onActionReady) onActionReady();
          return;
        }
        if (step.delayMs && step.delayMs > 0) {
          // Unsubscribe immediately so trigger doesn't fire again
          if (currentUnsubscribe) { currentUnsubscribe(); currentUnsubscribe = null; }
          delayTimer = setTimeout(() => {
            delayTimer = null;
            completeCurrentStep(sectionIndex, stepIndex);
          }, step.delayMs);
        } else {
          completeCurrentStep(sectionIndex, stepIndex);
        }
      };

      // If step requires video to be loaded, wait for it
      if (step.waitForVideo && activeLessonDef?.preloadUrls?.length) {
        const urls = activeLessonDef.preloadUrls;
        const allReady = urls.every((u) => isVideoReady(u));
        if (allReady) {
          doComplete();
        } else {
          // Unsubscribe the trigger so it doesn't fire again while we wait
          if (currentUnsubscribe) { currentUnsubscribe(); currentUnsubscribe = null; }
          // Poll until video is ready (check every 250ms, timeout 15s)
          let elapsed = 0;
          const poll = setInterval(() => {
            elapsed += 250;
            if (urls.every((u) => isVideoReady(u)) || elapsed >= 15000) {
              clearInterval(poll);
              doComplete();
            }
          }, 250);
          // Store cleanup for the poll
          const prevCleanup: (() => void) | null = currentUnsubscribe;
          currentUnsubscribe = () => {
            clearInterval(poll);
            if (prevCleanup) prevCleanup();
          };
        }
      } else {
        doComplete();
      }
    };

    currentUnsubscribe = eventBus.on(trigger.eventName, handler as any);
  }
  // Info steps don't have triggers — user clicks Next in instruction panel
}

export function advanceInfoStep(): void {
  if (!activeLessonDef) return;
  const state = enterpriseStore.getState();
  completeCurrentStep(state.currentSectionIndex, state.currentStepIndex);
}

export function skipStep(): void {
  if (!activeLessonDef) return;
  const state = enterpriseStore.getState();
  completeCurrentStep(state.currentSectionIndex, state.currentStepIndex);
}

function completeCurrentStep(sectionIndex: number, stepIndex: number): void {
  if (!activeLessonDef) return;
  const sections = activeLessonDef.sections ?? [];

  cleanup();

  const section = sections[sectionIndex];
  if (!section) return;
  const step = section.steps[stepIndex];
  if (step) {
    enterpriseStore.getState().completeStep(step.id);
  }

  // Next step in section
  const nextStepIndex = stepIndex + 1;
  if (nextStepIndex < section.steps.length) {
    activateStep(sectionIndex, nextStepIndex);
  } else {
    // Section complete — move to next section
    const nextSection = sectionIndex + 1;
    if (nextSection < sections.length) {
      activateStep(nextSection, 0);
    } else {
      completeEntireLesson();
    }
  }
}

function completeEntireLesson(): void {
  cleanup(true);
  teardownChecklist();
  // Tell the video board the lesson is done — shows completion card instead of just clearing
  const lessonName = activeLessonDef?.name ?? 'Lesson';
  broadcastLessonComplete(lessonName);
  enterpriseStore.getState().completeLesson();
  if (onLessonComplete) {
    onLessonComplete();
  }
}

function cleanup(forceStopAudio = false): void {
  if (currentUnsubscribe) {
    currentUnsubscribe();
    currentUnsubscribe = null;
  }
  if (focusSimCleanup) {
    focusSimCleanup();
    focusSimCleanup = null;
  }
  if (delayTimer) {
    clearTimeout(delayTimer);
    delayTimer = null;
  }
  removeHighlight();
  removeDisabled();
  removeLockdown();
  // Only force-stop audio on lesson complete/exit — let it play through between steps
  if (forceStopAudio) {
    stopAudio();
  }
}

function teardownChecklist(): void {
  for (const off of checklistUnsubscribes) {
    try { off(); } catch {}
  }
  checklistUnsubscribes = [];
}

export function stopEngine(): void {
  cleanup(true);
  teardownChecklist();
  cleanupPreloadedVideos();
  activeLessonDef = null;
  onStepActivated = null;
  onLessonComplete = null;
  enterpriseStore.getState().exitLesson();
}

export function getTotalStepCount(): number {
  if (!activeLessonDef) return 0;
  if (activeLessonDef.mode === 'checklist') return activeLessonDef.tasks?.length ?? 0;
  return (activeLessonDef.sections ?? []).reduce((sum, s) => sum + s.steps.length, 0);
}

export function getCompletedStepCount(): number {
  return enterpriseStore.getState().completedStepIds.length;
}

// ── Step-disabled element helpers ───────────────────────────────────
const DISABLED_CLASS = 'ent-step-disabled';

function applyDisabled(selectors: string[]): void {
  for (const sel of selectors) {
    document.querySelectorAll(sel).forEach((el) => el.classList.add(DISABLED_CLASS));
  }
}

function removeDisabled(): void {
  document.querySelectorAll(`.${DISABLED_CLASS}`).forEach((el) => el.classList.remove(DISABLED_CLASS));
}
